import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deviceDetailPath } from '@/features/devices/utils/device-navigation';
import { DoorOpen, Wifi, WifiOff, AlertTriangle, Link as LinkIcon, Unlink, ChevronLeft, ChevronRight, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Modal } from '@/components/ui/modal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { formatRelativeTime } from '@/lib/utils';
import { DeviceStatus, UserRole } from '@/types/api';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import type { Unit, Device, MeterReading } from '@/types/api';
import { getUnit, getUnits } from '../api/units-api';
import { sortUnitsForDisplay } from '../utils/sort-units-for-display';
import { useDataCache } from '@/store/data-cache-context';
import { getDevices, getDeviceMeterReadings, updateDevice, unassignDevice } from '@/features/devices/api/devices-api';
import { deviceTypeLabel, deviceStatusDisplay } from '@/lib/display-labels';

const STALE_MS = 24 * 60 * 60 * 1000;

const statusBadge: Record<DeviceStatus, 'success' | 'warning' | 'default' | 'error'> = {
  [DeviceStatus.Active]: 'success',
  [DeviceStatus.Provisioned]: 'warning',
  [DeviceStatus.Inactive]: 'default',
  [DeviceStatus.Error]: 'error',
};

export function UnitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const { invalidate } = useDataCache();
  const [unit, setUnit] = useState<Unit | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartReadings, setChartReadings] = useState<MeterReading[]>([]);
  const [siblingUnits, setSiblingUnits] = useState<Unit[]>([]);

  // Assign modal
  const [showAssign, setShowAssign] = useState(false);
  const [unassignedDevices, setUnassignedDevices] = useState<Device[]>([]);
  const [assignDeviceId, setAssignDeviceId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // Unassign
  const [unassigningDevice, setUnassigningDevice] = useState<Device | null>(null);

  // Replace — swap this unit's current device for a different (unassigned) one
  const [replacingDevice, setReplacingDevice] = useState<Device | null>(null);
  const [replacementDeviceId, setReplacementDeviceId] = useState('');
  const [replacementCandidates, setReplacementCandidates] = useState<Device[]>([]);
  const [replaceLoading, setReplaceLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadData(id);
    const interval = setInterval(() => refreshData(id), 10_000);
    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!unit?.buildingId) return;
    getUnits({ buildingId: unit.buildingId, perPage: 200 })
      .then((res) => setSiblingUnits(sortUnitsForDisplay(res.items)))
      .catch(() => setSiblingUnits([]));
  }, [unit?.buildingId]);

  const { prevUnit, nextUnit } = useMemo(() => {
    if (!unit || siblingUnits.length === 0) {
      return { prevUnit: null as Unit | null, nextUnit: null as Unit | null };
    }
    const idx = siblingUnits.findIndex((u) => u.id === unit.id);
    return {
      prevUnit: idx > 0 ? siblingUnits[idx - 1] : null,
      nextUnit: idx >= 0 && idx < siblingUnits.length - 1 ? siblingUnits[idx + 1] : null,
    };
  }, [unit, siblingUnits]);

  async function refreshData(unitId: string) {
    try {
      const [unitRes, devicesRes] = await Promise.all([
        getUnit(unitId),
        getDevices({ unitId, perPage: 100 }),
      ]);
      if (unitRes.success && unitRes.data) setUnit(unitRes.data);
      setDevices(devicesRes.items);

      const allReadings = await Promise.all(
        devicesRes.items.map(d =>
          getDeviceMeterReadings(d.id, { page: 1, perPage: 500, unitId })
            .then(r => r.items)
            .catch(() => [] as MeterReading[])
        )
      );
      setChartReadings(
        allReadings.flat().sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      );
    } catch {
      // silent
    }
  }

  async function loadData(unitId: string) {
    setLoading(true);
    try {
      const [unitRes, devicesRes] = await Promise.all([
        getUnit(unitId),
        getDevices({ unitId, perPage: 100 }),
      ]);
      if (unitRes.success && unitRes.data) setUnit(unitRes.data);
      setDevices(devicesRes.items);

      const allReadings = await Promise.all(
        devicesRes.items.map(d =>
          getDeviceMeterReadings(d.id, { page: 1, perPage: 500, unitId })
            .then(r => r.items)
            .catch(() => [] as MeterReading[])
        )
      );
      setChartReadings(
        allReadings.flat().sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      );
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function openAssignModal() {
    setAssignDeviceId('');
    setShowAssign(true);
    try {
      const res = await getDevices({ perPage: 100 });
      setUnassignedDevices(res.items.filter(d => !d.unitId));
    } catch {
      setUnassignedDevices([]);
    }
  }

  async function handleAssign() {
    if (!id || !assignDeviceId) return;
    setAssignLoading(true);
    try {
      await updateDevice(assignDeviceId, { unitId: id });
      invalidate(['devices', 'units', 'properties']);
      setShowAssign(false);
      loadData(id);
    } catch {
      // silent
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleUnassign() {
    if (!unassigningDevice || !id) return;
    const dev = unassigningDevice;
    setUnassigningDevice(null);
    try {
      await unassignDevice(dev.id);
      invalidate(['devices', 'units', 'properties']);
      loadData(id);
    } catch {
      // silent
    }
  }

  async function openReplaceModal(device: Device) {
    setReplacementDeviceId('');
    setReplacingDevice(device);
    try {
      const res = await getDevices({ perPage: 100 });
      // Same "unassigned" list the Assign Device modal uses. Exclude the
      // current device as a safety net in case it shows up without a unitId
      // during a race.
      setReplacementCandidates(res.items.filter((d) => !d.unitId && d.id !== device.id));
    } catch {
      setReplacementCandidates([]);
    }
  }

  async function handleReplace() {
    if (!replacingDevice || !replacementDeviceId || !id) return;
    const current = replacingDevice;
    setReplaceLoading(true);
    try {
      // Unassign the current device first so the unit isn't momentarily
      // carrying two devices. If the second call fails the unit ends up
      // empty rather than double-assigned, which is the safer failure mode.
      await unassignDevice(current.id);
      await updateDevice(replacementDeviceId, { unitId: id });
      invalidate(['devices', 'units', 'properties']);
      setReplacingDevice(null);
      loadData(id);
    } catch {
      // silent — user can retry
    } finally {
      setReplaceLoading(false);
    }
  }

  if (loading) return <LoadingPage />;
  if (!unit) {
    return (
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-500">Unit not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Back</Button>
        </div>
      </div>
    );
  }

  const hasDevices = devices.length > 0;
  const latestSeen = devices
    .map(d => d.lastSeenAt)
    .filter((v): v is string => !!v)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  const latestReading = chartReadings.length > 0 ? chartReadings[chartReadings.length - 1] : null;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Breadcrumb items={[
            { label: 'Properties', href: '/properties' },
            { label: unit.propertyName, href: `/properties/${unit.propertyId}` },
            { label: `Unit ${unit.unitNumber}` },
          ]} />

          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <div className="flex gap-1 mt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => prevUnit && navigate(`/units/${prevUnit.id}`)}
                  disabled={!prevUnit}
                  title={prevUnit ? `Previous: Unit ${prevUnit.unitNumber}` : 'No previous unit'}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => nextUnit && navigate(`/units/${nextUnit.id}`)}
                  disabled={!nextUnit}
                  title={nextUnit ? `Next: Unit ${nextUnit.unitNumber}` : 'No next unit'}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{unit.unitNumber}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{unit.buildingName}</p>
              </div>
            </div>
            {canWrite && (
              <Button variant="outline" onClick={openAssignModal}>
                <LinkIcon className="w-4 h-4" />
                Assign Device
              </Button>
            )}
          </div>

          {!hasDevices ? (
            <Card variant="elevated">
              <EmptyState
                icon={<DoorOpen className="w-10 h-10" />}
                title="No meters assigned"
                description="Assign a meter to this unit to start tracking usage."
                action={{ label: 'Assign Device', onClick: openAssignModal }}
              />
            </Card>
          ) : (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card variant="elevated">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Last Transmission</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                      {latestSeen ? formatRelativeTime(latestSeen) : 'Never'}
                    </p>
                  </CardContent>
                </Card>
                <Card variant="elevated">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Latest Reading</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1 font-mono">
                      {latestReading
                        ? `${(latestReading.computedWaterTotalization ?? latestReading.waterTotalization).toFixed(2)} ${latestReading.computedUnitOfMeasure ?? latestReading.unitOfMeasure}`
                        : '—'}
                    </p>
                  </CardContent>
                </Card>
                <Card variant="elevated">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Temperature</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                      {latestReading?.temperatureF != null ? `${latestReading.temperatureF.toFixed(1)} °F` : '—'}
                    </p>
                  </CardContent>
                </Card>
                <Card variant="elevated">
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Readings</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                      {chartReadings.length}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Meter card(s) */}
              <div className="flex items-center gap-4 px-5 text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                <span className="w-4" />
                <span className="flex-1">Meter</span>
                <span className="w-20 text-center hidden sm:inline">Status</span>
                <span className="w-24 hidden sm:inline">Type</span>
                <span className="w-24 hidden sm:inline">Last Seen</span>
                <span className="w-8" />
              </div>
              <div className="space-y-2">
                {devices.map((device) => {
                  const recent = device.lastSeenAt && (Date.now() - new Date(device.lastSeenAt).getTime()) < STALE_MS;
                  const icon =
                    device.status === DeviceStatus.Error ? <AlertTriangle className="w-4 h-4 text-error-500" /> :
                    recent ? <Wifi className="w-4 h-4 text-success-500" /> :
                    <WifiOff className="w-4 h-4 text-gray-400" />;

                  return (
                    <Card key={device.id} variant="elevated" padding="none">
                      <div className="flex items-center gap-4 px-5 py-3">
                        <span className="flex-shrink-0">{icon}</span>
                        <button
                          className="flex-1 min-w-0 text-left hover:underline truncate"
                          onClick={() => navigate(deviceDetailPath(
                            device.id,
                            id ? { kind: 'unit', id } : undefined,
                          ))}
                        >
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{device.serialNumber}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 font-mono">{device.devEui}</span>
                        </button>
                        <span className="flex-shrink-0">
                          <Badge variant={statusBadge[device.status]} size="sm" dot>
                            {deviceStatusDisplay(device.status, device.joinedAt)}
                          </Badge>
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                          {deviceTypeLabel[device.deviceType] ?? device.deviceType}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline whitespace-nowrap">
                          {device.lastSeenAt ? formatRelativeTime(device.lastSeenAt) : 'Never seen'}
                        </span>
                        {canWrite && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 flex-shrink-0"
                              onClick={() => openReplaceModal(device)}
                              title="Replace with another meter"
                            >
                              <ArrowRightLeft className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-error-600 hover:text-error-700 flex-shrink-0"
                              onClick={() => setUnassigningDevice(device)}
                              title="Unassign"
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Assign modal */}
      <Modal isOpen={showAssign} onClose={() => setShowAssign(false)} title="Assign Device to Unit" size="sm">
        <div className="space-y-4">
          <Select
            label="Unassigned Meter"
            value={assignDeviceId}
            onChange={setAssignDeviceId}
            options={[
              { value: '', label: 'Select a meter...' },
              ...unassignedDevices.map(d => ({
                value: d.id,
                label: `${d.serialNumber} (${d.devEui})`,
              })),
            ]}
            searchable
          />
          {unassignedDevices.length === 0 && !assignLoading && (
            <p className="text-sm text-gray-500 dark:text-gray-400">No unassigned meters available.</p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!assignDeviceId} isLoading={assignLoading}>
              Assign
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unassign confirm */}
      <ConfirmDialog
        isOpen={!!unassigningDevice}
        title="Unassign Meter"
        description={`Unassign "${unassigningDevice?.serialNumber}" from this unit? Readings from before this point will no longer be visible for this unit.`}
        confirmLabel="Unassign"
        onConfirm={handleUnassign}
        onCancel={() => setUnassigningDevice(null)}
      />

      {/* Replace modal — swap this unit's current device for a different one */}
      <Modal
        isOpen={!!replacingDevice}
        onClose={() => setReplacingDevice(null)}
        title="Replace Meter"
        description={
          replacingDevice
            ? `Replace "${replacingDevice.serialNumber}" on Unit ${unit?.unitNumber ?? ''} with a different meter. The current meter will be unassigned.`
            : undefined
        }
        size="sm"
      >
        <div className="space-y-4">
          <Select
            label="Replacement Meter"
            value={replacementDeviceId}
            onChange={setReplacementDeviceId}
            options={[
              { value: '', label: 'Select a meter…' },
              ...replacementCandidates.map((d) => ({
                value: d.id,
                label: `${d.serialNumber} (${d.devEui})`,
              })),
            ]}
            searchable
          />
          {replacementCandidates.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No unassigned meters available. Unassign one elsewhere or add a new meter first.
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setReplacingDevice(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleReplace}
              disabled={!replacementDeviceId}
              isLoading={replaceLoading}
            >
              Replace
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
